import { ActivityFeed, type ActivityDestination } from "@gladis/domain-audit/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { Page } from "../../../components/page.js";
import { useT } from "@gladis/ui/i18n";

export const Route = createFileRoute("/_authenticated/_shell/activity")({
  component: Activity,
});

/**
 * Activity — who did what, and when.
 *
 * Composition only. The feed, its paging and its one filter come from the audit package, which owns what an
 * activity entry means. This route decides where it appears and, below, where a row leads.
 *
 * It composes NOTHING ELSE, and `activity-composes-nothing.test.ts` enforces that. The dependency rules do
 * not: `apps/app` may legitimately import several domain `/ui` packages, so nothing but a test stops this
 * page growing a savings card because the data was one import away.
 */
function Activity(): ReactNode {
  const t = useT();
  const navigate = useNavigate();

  return (
    <Page>
      <Page.Title>{t("nav.activity")}</Page.Title>
      <Page.Body>
        <ActivityFeed
          onOpen={(destination) => {
            void navigate(routeFor(destination));
          }}
        />
      </Page.Body>
    </Page>
  );
}

/**
 * The one place in the codebase that knows both an activity destination and a URL.
 *
 * ==========================================================================================
 * WHY THE SWITCH IS HERE AND NOT IN THE AUDIT PACKAGE
 * ==========================================================================================
 *
 * Routes are the application's, not a bounded context's. `audit` resolves a row to a `record`, a `module` or
 * `none` — facts about the domain — and this function turns those into paths. Putting the paths in the
 * domain package would make it depend on the router and on the shape of every other module's routing, which
 * is the coupling `/ui` exists to avoid.
 *
 * ==========================================================================================
 * EXHAUSTIVE BY CONSTRUCTION
 * ==========================================================================================
 *
 * Every arm returns, and the `never` at the end means a thirteenth `ModuleName` — or a fourth destination
 * kind — is a COMPILE ERROR here rather than a row that silently navigates nowhere. That is the other half
 * of FR-014: the audit package's `Record` makes a missing destination rule impossible, and this makes a
 * destination with no route impossible.
 *
 * `none` never reaches this function — `ActivityRow` renders such a row inert rather than as a control — but
 * it is handled anyway, because "unreachable" is a claim about today's caller.
 */
function routeFor(destination: ActivityDestination): {
  to: string;
  params?: Record<string, string>;
} {
  switch (destination.kind) {
    case "record": {
      const module = destination.module;
      switch (module) {
        case "savings":
          return { to: "/savings/$accountId", params: { accountId: destination.recordId } };
        case "properties":
          return { to: "/properties/$propertyId", params: { propertyId: destination.recordId } };
        case "investments":
          return { to: "/investments/$stockId", params: { stockId: destination.recordId } };
        default:
          return exhausted(module);
      }
    }
    case "module": {
      const module = destination.module;
      switch (module) {
        case "savings":
          return { to: "/savings" };
        case "properties":
          return { to: "/properties" };
        case "investments":
          return { to: "/investments" };
        case "expenses":
          return { to: "/expenses" };
        case "cards":
          return { to: "/cards" };
        case "income":
          return { to: "/income" };
        case "vehicles":
          /*
           * A VALUATION HAS NO PAGE, so it resolves to the module — 018's rule that "a destination is
           * a place, not necessarily a record". Sending it to a detail route would oblige the feed to
           * resolve one that does not exist.
           */
          return { to: "/vehicles" };
        case "settings":
          // The household half: this module covers household, membership and invite events, and
          // `/settings` itself is only a redirect to it since the page was split in two.
          return { to: "/settings/household" };
        default:
          return exhausted(module);
      }
    }
    case "none":
      // Not reachable from the row, which renders an inert element instead. Staying put is the honest
      // answer if it ever is: there is nowhere to go.
      return { to: "/activity" };
    default:
      return exhausted(destination);
  }
}

/** Turns an unhandled case into a compile error rather than a silent fall-through. */
function exhausted(value: never): never {
  throw new Error(`unhandled activity destination: ${JSON.stringify(value)}`);
}
